/** Narration uses server-generated audio. Missing or failed audio stays in captions. */
import type { DirectorAudio, DirectorLanguage } from '@clutch/core';
import { pick } from './i18n';

export type VoiceMode =
  | { kind: 'provider'; audio: DirectorAudio }
  | { kind: 'captions'; reason: 'unavailable' };

/** Synthetic narration is disclosed; browser voices are never silently substituted. */
export function voiceLabel(m: VoiceMode, l: DirectorLanguage): string {
  if (m.kind === 'provider') {
    const label = pick({ en: 'AI voice', hi: 'AI आवाज़', zh: 'AI语音' }, l);
    return m.audio.provider === 'elevenlabs' ? `ElevenLabs · ${label}` : label;
  }
  return pick({ en: 'Captions only', hi: 'सिर्फ़ कैप्शन', zh: '仅字幕' }, l);
}

export function providerCovers(audio: DirectorAudio | undefined, segmentIds: readonly string[]): audio is DirectorAudio {
  if (!audio || audio.status !== 'ready' || !segmentIds.length || !audio.segments.length) return false;
  const have = new Set(audio.segments.filter((s) => s.url && Number.isFinite(s.durationMs) && s.durationMs > 0).map((s) => s.segmentId));
  return segmentIds.every((id) => have.has(id));
}

export function chooseVoice(o: { audio?: DirectorAudio; segmentIds: readonly string[] }): VoiceMode {
  return providerCovers(o.audio, o.segmentIds) ? { kind: 'provider', audio: o.audio } : { kind: 'captions', reason: 'unavailable' };
}

/** Silence between aligned phrases holds the previous caption, never a future final line. */
export function captionAtAudioTime(captions: readonly { startMs: number; endMs: number; text: string }[], ms: number) {
  return captions.filter((c) => Number.isFinite(c.startMs) && c.startMs <= ms).sort((a, b) => a.startMs - b.startMs).at(-1);
}

/** Cache successful synthesis only; a resolved unavailable response must remain retryable. */
export class SpeechCache {
  private readonly pending = new Map<string, Promise<DirectorAudio>>();
  get(key: string, segmentIds: readonly string[], load: () => Promise<DirectorAudio>): Promise<DirectorAudio> {
    const existing = this.pending.get(key);
    if (existing) return existing;
    const request = Promise.resolve().then(load);
    this.pending.set(key, request);
    const clear = () => { if (this.pending.get(key) === request) this.pending.delete(key); };
    void request.then((audio) => { if (!providerCovers(audio, segmentIds)) clear(); }, clear);
    return request;
  }
}

/** Ignore late provider results after closing the player; a hung request cannot hold it forever. */
export function waitForSpeech(pending: Promise<DirectorAudio | undefined>, signal: AbortSignal, timeoutMs = 150_000): Promise<DirectorAudio | undefined> {
  if (signal.aborted) return Promise.resolve(undefined);
  return new Promise((resolve) => {
    let settled = false;
    const done = (audio?: DirectorAudio) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      signal.removeEventListener('abort', abort);
      resolve(audio);
    };
    const abort = () => done();
    const timer = setTimeout(abort, timeoutMs);
    signal.addEventListener('abort', abort, { once: true });
    void pending.then((audio) => done(signal.aborted ? undefined : audio), abort);
  });
}

/** A playback watchdog spends its budget only while playing, never during a fan's pause. */
export function playbackDeadline(timeoutMs: number, onTimeout: () => void) {
  let remaining = timeoutMs;
  let since = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let stopped = false;
  const pause = () => {
    if (timer === undefined) return;
    clearTimeout(timer);
    timer = undefined;
    remaining = Math.max(0, remaining - (Date.now() - since));
  };
  return {
    setPlaying(active: boolean) {
      if (stopped) return;
      if (!active) return pause();
      if (timer !== undefined) return;
      since = Date.now();
      timer = setTimeout(() => {
        timer = undefined;
        stopped = true;
        onTimeout();
      }, remaining);
    },
    cancel() {
      pause();
      stopped = true;
    },
  };
}
